import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { FileUploader } from "@/components/ui/file-uploader";
import { getInitials } from "@/lib/utils";
import { useState } from "react";

interface ProfilePhotoDialogProps {
  open: boolean;
  onClose: () => void;
  onUpload: (file: File) => Promise<void>;
  userName?: string;
  currentImageUrl?: string;
}

export function ProfilePhotoDialog({
  open,
  onClose,
  onUpload,
  userName = "",
  currentImageUrl,
}: ProfilePhotoDialogProps) {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  const displayedUrl = previewUrl ?? currentImageUrl;
  const initials = getInitials(userName);

  const handleFileSelect = (file: File) => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setSelectedFile(file);
    setPreviewUrl(URL.createObjectURL(file));
  };

  const reset = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setSelectedFile(null);
    setPreviewUrl(null);
    setIsUploading(false);
  };

  const handleUpload = async () => {
    if (!selectedFile) return;
    setIsUploading(true);
    try {
      await onUpload(selectedFile);
      reset();
      onClose();
    } catch {
      setIsUploading(false);
    }
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) handleClose();
      }}
    >
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Update Profile Photo</DialogTitle>
          <DialogDescription>
            Choose a new profile photo to upload. Supported formats: JPG, PNG,
            GIF
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col items-center gap-6 py-4">
          <div className="flex size-32 shrink-0 items-center justify-center overflow-hidden rounded-full bg-muted">
            {displayedUrl ? (
              <img
                key={displayedUrl}
                src={displayedUrl}
                alt="Profile photo preview"
                className="size-full object-cover"
              />
            ) : (
              <span className="font-heading text-2xl font-bold text-text-primary">
                {initials}
              </span>
            )}
          </div>
          <FileUploader
            onFileSelect={handleFileSelect}
            accept="image/*"
            maxSize={10 * 1024 * 1024}
            buttonText={
              selectedFile ? "Choose Different Photo" : "Choose Photo"
            }
            variant="outline"
          />
        </div>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={isUploading}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={() => void handleUpload()}
            disabled={!selectedFile || isUploading}
          >
            {isUploading ? "Uploading..." : "Upload"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
